/**
 * Consultants tab — outside consultants on one case, ops side
 * (plans/CONSULTANT_CASE_ACCESS_PLAN.md §3.3). Ops may share only while the
 * org has never signed in (the setup window); after that only the org's
 * admin shares or extends. Ops may revoke at any time.
 */
import { useEffect, useState } from 'react';
import { api } from '../../../api';
import ReasonField from '../../../components/ops_admin/ReasonField';
import CaseTypeSelect from '../../../components/CaseTypeSelect';

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString() : '—'; }

export default function OrgConsultantsTab({ org, opsRole }) {
  const [grants, setGrants] = useState([]);
  const [windowOpen, setWindowOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null); // 'create' | 'share' | null
  const canMutate = ['admin', 'owner'].includes(opsRole);

  async function load() {
    setLoading(true); setError(null);
    try {
      const d = await api.get(`/ops_admin/orgs/${org.id}/consultants`);
      setGrants(d.grants || []);
      setWindowOpen(!!d.setup_window_open);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [org.id]);

  if (loading) return <p className="loading-text">Loading consultants...</p>;
  if (error) return <p className="error-text">{error}</p>;

  return (
    <div className="ops-tab">
      <div className="ops-page-header">
        <h2>Outside consultants ({grants.filter(g => g.status === 'active').length} active)</h2>
        {canMutate && windowOpen && (
          <div className="button-row">
            <button className="btn btn-primary" onClick={() => setMode(mode === 'create' ? null : 'create')}>
              {mode === 'create' ? 'Cancel' : '+ Create case for consultant'}
            </button>
            <button className="btn btn-ghost" onClick={() => setMode(mode === 'share' ? null : 'share')}>
              {mode === 'share' ? 'Cancel' : 'Share an existing case'}
            </button>
          </div>
        )}
      </div>

      <p className="settings-hint" style={{ color: 'var(--text-muted, #6c757d)', fontSize: 12 }}>
        {windowOpen
          ? 'Setup window: this organization has not signed in yet. Ops may create a case and share it with a consultant; the org sees every grant on arrival.'
          : 'This organization has signed in. Only its admin can share or extend a case now (Settings → Members → Outside consultants). Ops can still end a grant.'}
      </p>

      {mode === 'create' && <CreateCaseForm orgId={org.id} onDone={() => { setMode(null); load(); }} />}
      {mode === 'share' && <ShareExistingForm orgId={org.id} onDone={() => { setMode(null); load(); }} />}

      <table className="ops-table">
        <thead>
          <tr><th>Consultant</th><th>Case</th><th>Status</th><th>Until</th><th>Issued by</th><th>Agreement</th>{canMutate && <th>Actions</th>}</tr>
        </thead>
        <tbody>
          {grants.length === 0 && <tr><td colSpan={7} className="empty-row">No consultants</td></tr>}
          {grants.map(g => (
            <GrantRow key={g.id} grant={g} orgId={org.id} canMutate={canMutate} onRefresh={load} redact={!!org.is_criminal_justice} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrantRow({ grant, orgId, canMutate, onRefresh, redact }) {
  const [revoking, setRevoking] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const active = grant.status === 'active' && grant.is_currently_active;

  async function revoke() {
    if (!reason.trim()) { setError('Reason required'); return; }
    setBusy(true); setError(null);
    try {
      await api.del(`/ops_admin/orgs/${orgId}/consultants/${grant.id}`, { reason });
      setRevoking(false);
      onRefresh();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <tr style={active ? undefined : { opacity: 0.6 }}>
      <td>{grant.display_name || '—'}<div className="mono" style={{ fontSize: 11 }}>{grant.email}</div></td>
      <td>{redact ? <span className="mono" style={{ fontSize: 11 }}>{grant.case_id}</span> : (grant.case_name || grant.case_id)}</td>
      <td>{active ? 'active' : grant.status}</td>
      <td>{fmtDate(grant.expires_at)}</td>
      <td>{grant.issued_by_kind}{grant.issued_by_email ? ` (${grant.issued_by_email})` : ''}</td>
      <td className="mono" style={{ fontSize: 11 }}>{redact ? '—' : grant.agreement_ref}</td>
      {canMutate && (
        <td>
          {active && !revoking && <button className="btn btn-sm btn-ghost" onClick={() => setRevoking(true)}>Revoke</button>}
          {revoking && (
            <div style={{ display: 'grid', gap: 6, minWidth: 260 }}>
              <ReasonField value={reason} onChange={setReason} placeholder="Why this access ends now" />
              <div className="button-row">
                <button className="btn btn-sm btn-danger" onClick={revoke} disabled={busy}>Confirm revoke</button>
                <button className="btn btn-sm btn-ghost" onClick={() => setRevoking(false)} disabled={busy}>Cancel</button>
              </div>
            </div>
          )}
          {error && <p className="error-text">{error}</p>}
        </td>
      )}
    </tr>
  );
}

function GrantFields({ form, set }) {
  return (
    <>
      <div className="form-group">
        <label className="form-label">Consultant's email *</label>
        <input className="form-input" type="email" required value={form.email} onChange={set('email')} placeholder="first.last.consulting@chargestack.ai" />
      </div>
      <div className="form-group">
        <label className="form-label">Days of access</label>
        <input className="form-input" type="number" min={1} max={365} value={form.days} onChange={set('days')} />
      </div>
      <div className="form-group">
        <label className="form-label">Agreement reference *</label>
        <input className="form-input" required value={form.agreement_ref} onChange={set('agreement_ref')} placeholder="Engagement letter / addendum reference" />
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
        <input type="checkbox" required checked={form.attested_screening} onChange={set('attested_screening')} style={{ marginTop: 3 }} />
        <span>The client has screened this person per its policy (fingerprint-based check and Security Addendum for a CJ agency).</span>
      </label>
      <ReasonField value={form.reason} onChange={v => set('reason')({ target: { value: v } })} placeholder="Why ops is sharing this case (the engagement, in a sentence)" />
    </>
  );
}

function useForm(initial) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  return [form, set];
}

function CreateCaseForm({ orgId, onDone }) {
  const [form, set] = useForm({ email: '', case_name: '', case_number: '', case_type: 'other', description: '',
    days: 90, reason: '', agreement_ref: '', attested_screening: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await api.post(`/ops_admin/orgs/${orgId}/consultant_cases`, { ...form, days: Number(form.days) });
      onDone();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="ops-form" style={{ display: 'grid', gap: 8, maxWidth: 640, margin: '8px 0 16px' }}>
      <h3 style={{ margin: 0 }}>Create a case and share it</h3>
      <div className="form-group">
        <label className="form-label">Case name *</label>
        <input className="form-input" required value={form.case_name} onChange={set('case_name')} placeholder="State v. Smith" />
      </div>
      <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
        <div className="form-group">
          <label className="form-label">Case number</label>
          <input className="form-input" value={form.case_number} onChange={set('case_number')} placeholder="DC-26-123" />
        </div>
        <div className="form-group">
          <label className="form-label">Case type</label>
          <CaseTypeSelect value={form.case_type} onChange={v => set('case_type')({ target: { value: v } })} />
        </div>
      </div>
      <GrantFields form={form} set={set} />
      <div className="button-row">
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create and share'}</button>
      </div>
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}

function ShareExistingForm({ orgId, onDone }) {
  const [form, set] = useForm({ email: '', case_id: '', days: 90, reason: '', agreement_ref: '', attested_screening: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await api.post(`/ops_admin/orgs/${orgId}/consultants`, { ...form, days: Number(form.days) });
      onDone();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="ops-form" style={{ display: 'grid', gap: 8, maxWidth: 640, margin: '8px 0 16px' }}>
      <h3 style={{ margin: 0 }}>Share an existing case</h3>
      <div className="form-group">
        <label className="form-label">Case id *</label>
        <input className="form-input mono" required value={form.case_id} onChange={set('case_id')} placeholder="uuid of a case in this org" />
      </div>
      <GrantFields form={form} set={set} />
      <div className="button-row">
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Sharing…' : 'Share'}</button>
      </div>
      {error && <p className="error-text">{error}</p>}
    </form>
  );
}
